'use client';

import { ChevronDown } from '@lulalife/design-system/icons/line';
import { type ReactNode, useId, useState } from 'react';

type SettingsDisclosureProps = {
  /** Visible trigger copy, e.g. `Details`. */
  label: string;
  /**
   * Accessible name for the trigger. Pass it whenever a page renders more than
   * one disclosure — a screen-reader user tabbing through forty identical
   * "Details" buttons has no way to tell which row each belongs to.
   */
  ariaLabel?: string;
  children: ReactNode;
  'data-testid'?: string;
  className?: string;
};

/**
 * A quiet expandable panel for secondary detail — the WAI-ARIA disclosure
 * pattern, closed on first render.
 *
 * DS-GAP(Accordion): delete this when DS ships a disclosure/accordion primitive.
 *
 * The panel stays in the DOM behind the `hidden` attribute rather than being
 * unmounted, so `aria-controls` always resolves to a real element and the
 * expanded/collapsed state is the only thing that changes. Nothing here sets a
 * `display` utility on the panel — one would outrank the UA stylesheet's
 * `[hidden] { display: none }` and leave the panel permanently open.
 */
export const SettingsDisclosure = ({
  label,
  ariaLabel,
  children,
  className,
  'data-testid': testId,
}: SettingsDisclosureProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `${useId()}-disclosure-panel`;

  return (
    <div
      data-lula-ds=""
      className={`flex flex-col gap-space-8 font-sans${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={() => setIsOpen((open) => !open)}
        // `border-0`, `bg-transparent` and `p-0` are names Bootstrap also emits,
        // unlayered and important (see section 4 of design-system-compat.css), so
        // Bootstrap's declaration is what actually applies here. Checked against
        // the compiled Sass: its values are `border: 0`, `background-color:
        // transparent` and `padding: 0` — identical to what these utilities want,
        // so no important modifier is needed. Anything that wants a DIFFERENT
        // value for one of those properties on this element does need one.
        className="typography-label-sm-strong inline-flex w-fit cursor-pointer items-center gap-space-4 border-0 bg-transparent p-0 text-text-tertiary hover:text-text-secondary focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
      >
        <span
          aria-hidden="true"
          className={`inline-flex size-space-16 shrink-0${isOpen ? ' rotate-180' : ''}`}
        >
          <ChevronDown width="100%" height="100%" />
        </span>
        {label}
      </button>
      <div
        id={panelId}
        hidden={!isOpen}
        // The display utility is applied ONLY while open. A `flex` that stayed on
        // the closed panel would outrank the UA stylesheet's
        // `[hidden] { display: none }` and leave it permanently visible.
        className={`typography-body-xs min-w-0 text-text-tertiary${
          isOpen ? ' flex flex-col gap-space-4' : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default SettingsDisclosure;
